import * as React from 'react';
import { useParams } from 'react-router-dom';
import { PublicArticle } from '../../../../amorg-common';
import { getPublicArticle } from '../../../../api/siteArticlesClientApi';
import { Card } from '../../../../designSystem/Card';
import { Label } from '../../../../designSystem/Label';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../../designSystem/MessageCard';
import { PageHeader } from '../../../../designSystem/PageHeader';
import { ImportanceAvatar } from '../../../../designSystem/avatar/ImportanceAvatar';
import { Flex } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { HtmlEditorViewer } from '../../../../designSystem/inputs/HtmlEditorViewer';
import { SingleColumnLayout } from '../../../_layouts/SingleColumnLayout';
import { ArticleVersionFields } from '../../../_components/articles/ArticleVersionFields';
import { ArticleVersionHeader } from '../../../_components/articles/ArticleVersionHeader';
import { PublicPageWrapper } from '../../../_pageWrappers/PublicPageWrapper';
import { AppendixPanel } from '../../[articleId]/_components/AppendixPanel';

export function PublicArticlePage(): JSX.Element
{
    const { siteId, articleId } = useParams<{ siteId:string, articleId: string; }>();

    if(!siteId || !articleId)
        return <></>;

    const [loading, setLoading] = React.useState(true);
    const [articleVersions, setArticleVersions] = React.useState<PublicArticle[] | null>(null);
    const [articleIndex, setArticleIndex] = React.useState(0);

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const result = await getPublicArticle(articleId, siteId);
            setArticleVersions(result);
            setLoading(false);
        };
        getData();
    }, []);

    const article = articleVersions && articleVersions.length > 0
        ? articleVersions[articleIndex]
        : null;

    return (
        <PublicPageWrapper>
            <PageHeader
                headerIcon='balance'
                headerText='Lovservice'
                narrow />
            <SingleColumnLayout narrow>
                <Flex flexDirection='column' gap={16}>
                    <LoadingPageOverlay loading={loading} />

                    {!loading && articleVersions === null &&
                        <MessageCard variant='error'>
                            Lovkravet kunne ikke hentes. Kontroller at funktionen er aktiveret for sitet og at linket er gyldigt.
                        </MessageCard>
                    }

                    {article &&
                        <PublicArticleVersionPanel
                            siteId={siteId}
                            article={article}
                            versions={articleVersions?.map(av => av.version) || []}
                            onVersionChange={version => setArticleIndex(articleVersions?.findIndex(av => av.version === version) || 0)}
                        />
                    }
                </Flex>
            </SingleColumnLayout>
        </PublicPageWrapper>
    );
}

type PublicArticleVersionPanelProps = {
    siteId:string,
    article: PublicArticle,
    versions: number[],
    onVersionChange: (version: number) => void,
};

function PublicArticleVersionPanel(props: PublicArticleVersionPanelProps): JSX.Element
{
    const latestVersion = Math.max(...props.versions);

    return (
        <Flex flexDirection='column' gap={16}>
            <Card variant='outlined'>
                <Flex flexDirection='column' gap={32}>
                    <ArticleVersionHeader
                        version={props.article.version}
                        releasedDate={props.article.release.releasedDate}
                        versions={props.versions}
                        onVersionChange={props.onVersionChange}
                    />

                    <ArticleVersionFields {...props.article} 
                        importance={props.article.publication?.importance}
                        selectedCategoryTag={props.article.categoryTag} 
                        disableEdit />
                </Flex>
            </Card>

            {props.article.appendices.length > 0 &&
                <AppendixPanel appendices={props.article.appendices} />
            }

            {props.article.version === latestVersion && props.article.customRequirements &&
                <Card variant='outlined'>
                    <Flex flexDirection='column' gap={16}>
                        <Label>Praksis for juridisk forpligtelse</Label>
                        
                        <Card variant='outlined'>
                            <HtmlEditorViewer content={props.article.customRequirements.requirementsText} />
                        </Card>
                    </Flex> 
                </Card>
            }

            <Card variant='outlined'>
                <Flex flexDirection='column' gap={16}>
                    <Flex alignItems='center' gap={8}>
                        <Label>Evaluering</Label>
                        <ImportanceAvatar mode="evaluation" importance={props.article.evaluation?.evaluationImportance || undefined} size="20px" />
                    </Flex>

                    {!props.article.evaluation?.evaluationText &&
                        <i>Der er ikke foretaget en evaluering</i>
                    }
                    
                    {props.article.evaluation?.evaluationText &&
                        <Card variant='outlined'>
                            <HtmlEditorViewer content={props.article.evaluation?.evaluationText || ''} />
                        </Card>
                    }
                </Flex> 
            </Card>
        </Flex>
    );
}